<template>
  <ExpandableCard :minHeightForMultiColumn="true" @show-more="onCardExpanded()" class="expandable-card bg-body-tertiary">
    <template #header>
      <span v-show="filtersApplied" class="filters-applied-note" >
        <i class="fa fa-filter"></i> filters applied
      </span>

      <!-- Slot is used for the header, generally an image with this provider's logo. -->
      <slot></slot>

    </template>

    <template #body>
      <LoadingSpinner v-if="waitingForResponse" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>

      <div v-else-if="totalFilmCount === 0" class="no-films-found">
        <p>
          no films found
          <span class="membership-message" v-if="!userStore.isFullMember()"><br>
            <router-link to="/membership">members</router-link> see films from <i>all</i> seasons<br>
<!--            <router-link to="/membership">learn more</router-link>-->
          </span>
        </p>

      </div>

      <div v-else class="row film-list">
        <!-- feature films in the left column -->
        <div v-if="featuresToDisplay.length > 0" class="" :class="(docsToDisplay.length || shortsToDisplay.length) ? 'col-6 left-column' : 'col-12 left-column' ">
          <ul class="list-unstyled">
            <span class="films-header">feature films</span>
            <li v-for="(displayFilm) in featuresToDisplay" :key="displayFilm._id" class="container">
              <FilmLink
                  :film_name="displayFilm.name"
                  :film_id="displayFilm._id"
                  :show_if_bookmarked="true"
                  :show_flag_for_language_code="!['en','xx'].includes(displayFilm.original_language) ? displayFilm.original_language : null"
                  :link_location="`Viewing Options Card: ${provider}`"
                  single_line="true"
              >
              </FilmLink>
            </li>
          </ul>
        </div>

        <!-- docs and shorts in the right column -->
        <div class="" :class="featuresToDisplay.length ? 'col-6 right-column' : 'col-12 left-column' ">
          <ul v-if="docsToDisplay.length > 0" class="list-unstyled">
            <span class="films-header">documentaries</span>
            <li v-for="(displayFilm) in docsToDisplay" :key="displayFilm._id" class="container" >
              <FilmLink
                  :film_name="displayFilm.name"
                  :film_id="displayFilm._id"
                  :show_if_bookmarked="true"
                  :show_flag_for_language_code="!['en','xx'].includes(displayFilm.original_language) ? displayFilm.original_language : null"
                  :link_location="`Viewing Options Card: ${provider}`"
                  single_line="true"
              >
              </FilmLink>
            </li>
          </ul>

          <hr v-if="(docsToDisplay.length > 0) && (shortsToDisplay.length > 0)">

          <ul v-if="shortsToDisplay.length > 0" class="list-unstyled">
            <span class="films-header">shorts</span>
            <li v-for="(displayFilm) in shortsToDisplay" :key="displayFilm._id" class="container">
              <FilmLink
                  :film_name="displayFilm.name"
                  :film_id="displayFilm._id"
                  :show_if_bookmarked="true"
                  :show_flag_for_language_code="!['en','xx'].includes(displayFilm.original_language) ? displayFilm.original_language : null"
                  :link_location="`Viewing Options Card: ${provider}`"
                  single_line="true"
              >
              </FilmLink>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <template #footer>
      <span v-if="last_update" class="last-update-date">
        <i class="fa fa-calendar-check-o" aria-hidden="true"></i> {{last_update}}
      </span>
    </template>

  </ExpandableCard>
</template>



<script>
import axios from "axios";
import {Film} from "@/film";
import FilmLink from "@/components/FilmLink.vue";
import { userStore } from "@/userStore";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import ExpandableCard from "@/components/ExpandableCard.vue";
import analytics from "@/analytics";

export default {
  name: "ScheduledFilmListCard",
  components: {LoadingSpinner, FilmLink, ExpandableCard},
  props: ['title', 'provider', 'providerId', 'hideViewedFilms', 'filterByGenres'],
  computed: {
    userStore() {
      return userStore;
    },
    totalFilmCount() {
      return (new Set([
          ...(this.featuresToDisplay || []),
          ...(this.docsToDisplay || []),
          ...(this.shortsToDisplay || [])
      ])).size;
    },
    featuresToDisplay() {
      return this.filterFilmList(this.films.features);
    },
    docsToDisplay() {
      return this.filterFilmList(this.films.docs);
    },
    shortsToDisplay() {
      return this.filterFilmList(this.films.shorts);
    },
    filtersApplied() {
      return ( this.hideViewedFilms || ( this.filterByGenres && this.filterByGenres.length > 0 ) );
    }
  },
  watch: {
    hideViewedFilms(newValue) {
      if ( newValue === true ) {
        this.saveUserViewedFilmsSnapshot();
      }
    },
  },
  data() {
    return {
      waitingForResponse: false,
      last_update: null,
      films: { features: [], docs: [], shorts: [] },
      showExpanded: false,
      expandedIndex: 8,
      userViewedFilmsSnapshot: []
    }
  },
  async created() {

    this.saveUserViewedFilmsSnapshot();

    // clear our display schedule
    this.films =  { features: [], docs: [], shorts: [] };
    this.waitingForResponse = true;

    // get the schedule for the given provider
    let requestUrl = `/api/viewing-options/streaming/${this.providerId}/films`;

    // add range for screenshots
    if ( this.$screenshotMode && Array.isArray(this.$screenshotModeSettings?.viewingOptionsYearRange) )
      requestUrl += this.$screenshotModeSettings?.viewingOptionsYearRange.reduce((accQueryParams, curYear, curIndex) => {
        return accQueryParams + (curIndex===0? '?' : '&') + `range=${curYear}`
      }, '');

    axios.get(requestUrl, {
      validateStatus: status => (status >= 200 && status < 300) || status === 404, // 404s are expected
      params: {
        catalogs: ['flatrate', 'free', 'ads']
      }
    })
      .then(response => {
        //console.log(`GET /api/viewing-options/streaming/${this.provider}/films`, response.data);

        const last_update_date = new Date(response.data.last_update);
        this.last_update = last_update_date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        const responseFilmList = (response.status === 404)? [] : response.data.films.map(_film => new Film(_film));

        //console.log(responseFilmList);

        // STRATEGY 1: List unviewed films at the top, in priority order; then viewed films (also in priority order)
        //
        // // separate the response films into ones we've viewed, and ones we haven't
        // const viewedFilms = [];
        // const unviewedFilms = [];
        // responseFilmList.forEach((responseFilm) => {
        //   const newFilm = new Film(responseFilm);
        //   if ( userStore.isFilmMarkedViewed(newFilm._id) ) {
        //     viewedFilms.push(newFilm);
        //   } else {
        //     unviewedFilms.push(newFilm);
        //   }
        // });
        // // sort both lists by display priority
        // Film.sortFilmsByPrioirity(viewedFilms);
        // Film.sortFilmsByPrioirity(unviewedFilms);
        //
        // // display as a single list, with the unviewed ones first
        // this.films = unviewedFilms.concat(viewedFilms);

        // STRATEGY 2: List all films chronologically (most recent first)
        //
        Film.sortFilmsChronologically(responseFilmList);
        responseFilmList.reverse();

        // STRATEGY 3: Sort by Priority (mostly for screenshots)
        //
        if ( this.$screenshotMode ) {
          Film.sortFilmsByPrioirity(responseFilmList);
        }

        // screenshots don't care about bookmarked flag sorting
        if ( !this.$screenshotMode ) {
          // bring all the unviewed-but-bookmarked films to the top
          responseFilmList.sort((film1, film2) => {
            if (userStore.isFilmBookmarkedAndUnviewed(film1._id) && !userStore.isFilmBookmarkedAndUnviewed(film2._id)) {
              return -1;
            }
            if (userStore.isFilmBookmarkedAndUnviewed(film2._id) && !userStore.isFilmBookmarkedAndUnviewed(film1._id)) {
              return 1;
            }
            return 0;
          });
        }

        // for ad screenshots, push foreign films to the bottom and this years films to the top
        if ( this.$screenshotMode ) {
          responseFilmList.sort((film1, film2) => {
            if ((film1.original_language === 'en') && (film2.original_language !== 'en')) {
              return -1;
            }
            if ((film2.original_language === 'en') && (film1.original_language !== 'en')) {
              return 1;
            }

            return 0;
          });
        }

        this.films.docs = responseFilmList.filter((film) => {
          return (
              film.genres.includes('Documentary') ||
              film.nominations.find(nom => nom.category.normalized_name.includes('Documentary'))
          );
        });
        this.films.shorts = responseFilmList.filter((film) => {
          return (
              (film.runtime < 41) || // Note that some early ceremonies doen't respect the same "less than 40 mins" rule, so we may change this.
              film.nominations.find(nom => nom.category.normalized_name.includes('Short'))
          );
        });
        this.films.features = responseFilmList.filter((film) => {
          return (
              !this.films.docs.find(doc => (doc._id === film._id)) &&
              !this.films.shorts.find(doc => (doc._id === film._id))
          );
        });
      })

      .catch(err => {
        analytics.notifyBugsnagOfError(err);
      })

      .finally(() => {
        this.waitingForResponse = false;
      });
  },

  methods: {
    saveUserViewedFilmsSnapshot() {
      // We cache the films a user has viewed. If viewed films are hidden, and something else (like a film modal) marks
      //  a film as viewed, we DON'T want the visibility to react--we just want it to show crossed-out.
      if ( userStore.user?.viewed ) {
        this.userViewedFilmsSnapshot = userStore.user?.viewed;
      }
    },

    filterFilmList(_filmList) {

      var filteredFilmList = _filmList;

      if ( this.hideViewedFilms ) {
        filteredFilmList = filteredFilmList.filter((film) => {
          return !this.userViewedFilmsSnapshot.includes(film._id);
        });
      }

      if ( this.filterByGenres && (this.filterByGenres.length > 0) ) {
        filteredFilmList = filteredFilmList.filter((film) => {
          return film.genres.find((filmGenre) => {
            return this.filterByGenres.includes(filmGenre);
          });
        });
      }

      return filteredFilmList;
    },

    onCardExpanded() {
      analytics.sendEvent('viewing_option_expanded', {
        provider: this.provider
      });
    }
  }
}

</script>


<style scoped>

  .expandable-card {
    margin-bottom: 20px;


    .last-update-date {
      position: absolute;
      bottom: 3px;
      left: 5px;
      pointer-events: none;
      user-select: none;
      color: var(--bs-tertiary-color);
      opacity: 50%;
      font-size: 8px;
      margin-top: -4px;
    }

    .filters-applied-note {
      position: absolute;
      top: 47px;
      right: 1em;
      color: var(--bs-primary);
      opacity: 80%;
      font-size: 9px;
      margin-top: -4px;
    }
  }

  .film-list {
    font-size: x-small;

    .films-header {
      font-size: small;
      font-weight: 700;
      color: var(--bs-tertiary-color);
    }

    ul {
      margin-bottom: 0;
    }

    li {
        /*line-height: 105%;*/
        display: block;
        padding-top: 1px;
        padding-bottom: 0px;
        padding-right: 0px;
        padding-left: 5px;
    }
  }

  .left-column {
    /*background-color: lightblue;*/
    padding-left: 0.5em;
    padding-right: 0;
  }

  .right-column {
    /*background-color: lightpink;*/
    padding-left: 0;
    padding-right: 0;
  }

  .no-films-found {
    margin-left: auto;
    margin-right: auto;
    width: 80%;
    text-align: center;
    font-size: small;
    font-weight: 700;
    color: var(--bs-tertiary-color);
    padding-top: 5px;
    padding-bottom: 2px;

    .membership-message {
      font-weight: normal;
      a {
        text-decoration: inherit;
        color: inherit;
        cursor: pointer;
        font-weight: 500;
        &:hover {
          color: var(--bs-link-hover-color);
        }
      }
    }
  }

</style>